import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import MatchCard from './MatchCard';
import WelcomeBonus from './WelcomeBonus';
import { formatMediumDate } from '../utils/formatDate';

// Маппинг лиг с красивыми названиями и флагами для сортировки
const LEAGUE_DISPLAY_NAMES = {
  'all': 'All',
  "ENGLAND_PREMIER_LEAGUE": "🏴󠁧󠁢󠁥󠁮󠁧󠁿 EPL",
  "EPL": "🏴󠁧󠁢󠁥󠁮󠁧󠁿 EPL",
  "SPAIN_LA_LIGA": "🇪🇸 LL",
  "GERMANY_BUNDESLIGA": "🇩🇪 BDL",
  "ITALY_SERIE_A": "🇮🇹 SA",
  "FRANCE_LIGUE_1": "🇫🇷 L1", 
  "FRANCE_LIGUE_ONE": "🇫🇷 L1",
  "UCL": "🇪🇺 UCL",
  "UEFA_CHAMPS_LEAGUE": "🇪🇺 UCL",
  "UEL": "🇪🇺 UEL",
  "FIFA_CLUB_WORLD_CUP": "🌍 FIFA WC",
  "UEFA_NATIONS_LEAGUE": "🌍 UEFA NL",
  "NETHERLANDS_EREDIVISIE": "🇳🇱 Eredivisie",
  "ENGLAND_EFL_CHAMPIONSHIP": "🇬🇧 EFL C", // Championship
  "EFL_CHAMP": "🇬🇧 EFL C", // Championship
  "ENGLAND_FA_CUP": "🇬🇧 FA C", // FA Cup
  "PORTUGAL_PRIMEIRA_LIGA": "🇵🇹 PL",
  "BELGIUM_FIRST_DIVISION_A": "🇧🇪 FD A",
  "SCOTLAND_PREMIERSHIP": "🏴󠁧󠁢󠁳󠁣󠁴󠁿 SP",
  "LEAGUE_OF_IRELAND": "🇮🇪 LOI",

  // новые лиги
  "POLAND_EKSTRAKLASA": "🇵🇱 EK",
  "CROATIA_HNL": "🇭🇷 HNL",
  "DENMARK_SUPERLIGA": "🇩🇰 SL",
  "SWEDEN_ALLSVENSKAN": "🇸🇪 AS",
  "TURKEY_SUPER_LIG": "🇹🇷 SL",
  "AUSTRIA_BUNDESLIGA": "🇦🇹 BDL",
  "SWITZERLAND_SUPER_LEAGUE": "🇨🇭 SL",
  "RUSSIA_PREMIER_LEAGUE": "🇷🇺 RPL",
  "NORWAY_ELITESERIEN": "🇳🇴 EL",
  "CYPRUS_FIRST_DIVISION": "🇨🇾 1D",
  "CZECH_FIRST_LEAGUE": "🇨🇿 1L",
  "HUNGARY_NB_I": "🇭🇺 NB I",
  "GREECE_SUPER_LEAGUE": "🇬🇷 SL",
  "ROMANIA_LIGA_I": "🇷🇴 L1",
  "SLOVAKIA_1_LIGA": "🇸🇰 1L",
  "SLOVENIA_PRVA_LIGA": "🇸🇮 PL",
  "BULGARIA_FIRST_LEAGUE": "🇧🇬 PL",
  "SERBIA_SUPER_LIGA": "🇷🇸 SL",
  "BOSNIA_PREMIER_LEAGUE": "🇧🇦 PL",
  "FINLAND_VEIKKAUSLIIGA": "🇫🇮 VLL",
  "GEORGIA_EROVNULI_LIGA": "🇬🇪 EL",
  "LATVIA_VIRSLIGA": "🇱🇻 VL",
  "UEFA_CONFERENCE_LEAGUE": "🇪🇺 CCL",
};

const PredictionList = React.memo(({ predictions, allPredictions: allPredictionsProp, sortType, onSortChange, showAllLeagues, onToggleAllLeagues }) => {
  
  // Создаем флаг, который изменится с false на true только один раз, когда придут данные.
  const hasData = allPredictionsProp && Object.keys(allPredictionsProp).length > 0;

  // Сортировка лиг по актуальности - лиги с более свежими матчами показываются первыми
  const availableLeagues = useMemo(() => {
    if (!hasData) {
      return [];
    }
    
    // Собираем информацию о лигах и их самых свежих матчах
    const leagueData = new Map();
    
    Object.values(allPredictionsProp).flat().forEach(prediction => {
      if (prediction && prediction.league && prediction.matchDate) {
        const league = prediction.league;
        const matchDate = new Date(prediction.matchDate);
        
        if (!leagueData.has(league) || matchDate > leagueData.get(league).latestDate) {
          leagueData.set(league, {
            league,
            latestDate: matchDate,
            matchCount: (leagueData.get(league)?.matchCount || 0) + 1
          });
        } else {
          // Обновляем только счетчик матчей
          const existing = leagueData.get(league);
          existing.matchCount += 1;
        }
      }
    });
    
    // Сортируем лиги по актуальности (самая свежая дата первой)
    return Array.from(leagueData.values())
      .sort((a, b) => {
        // Сначала по дате (более свежие первыми)
        const dateDiff = b.latestDate.getTime() - a.latestDate.getTime();
        if (dateDiff !== 0) return dateDiff;
        
        // Если даты одинаковые, то по количеству матчей (больше матчей = более активная лига)
        const countDiff = b.matchCount - a.matchCount;
        if (countDiff !== 0) return countDiff;
        
        // В крайнем случае - алфавитный порядок
        return a.league.localeCompare(b.league);
      })
      .map(item => item.league);
  }, [hasData, allPredictionsProp]);

  // Создаем стабильный список всех кнопок лиг
  const allLeagueButtons = useMemo(() => {
    if (!hasData) return [];
    
    // Кнопка "All" всегда первая
    const allButton = { value: 'time', label: 'All' };
    
    // Все доступные лиги преобразуем в кнопки
    const leagueButtons = availableLeagues.map(league => ({
      value: league,
      label: LEAGUE_DISPLAY_NAMES[league] || league
    }));

    return [allButton, ...leagueButtons];
  }, [availableLeagues, hasData]);

  // Логика для отображения кнопок - определяет сколько показать
  const getDisplayedLeagues = useMemo(() => {
    const maxVisible = 4; // Показываем максимум 4 кнопки перед "More"
    
    const hasMoreLeagues = allLeagueButtons.length > maxVisible;

    if (showAllLeagues && hasMoreLeagues) {
      return {
        visible: allLeagueButtons, // Показываем все кнопки
        hasMore: false
      };
    }

    return {
      visible: allLeagueButtons.slice(0, maxVisible), // Показываем первые N кнопок
      hasMore: hasMoreLeagues
    };
  }, [allLeagueButtons, showAllLeagues]);

  // Create a flattened array from the allPredictions object prop for findIndex
  const allPredictionsFlat = useMemo(() => {
    if (!allPredictionsProp) return [];
    // Object.values gives us [[predictionsForDay1], [predictionsForDay2]]
    // .flat() creates a single array: [prediction1, prediction2, ...]
    return Object.values(allPredictionsProp).flat();
  }, [allPredictionsProp]);

  // Если нет прогнозов, показываем сообщение
  if (!predictions || Object.keys(predictions).length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-10"
      >
        <p className="text-gray-500 dark:text-gray-400">No predictions available yet.</p>
        <p className="text-sm text-gray-400 dark:text-gray-500 mt-2">
          Check back later for new predictions.
        </p>
      </motion.div>
    );
  }

  const { visible: displayedLeagues, hasMore } = getDisplayedLeagues;

  return (
    <div className="pt-2">
      {/* Tournament Filter Buttons */}
      {availableLeagues.length > 0 && (
        <div className="mb-6">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 md:p-4 shadow-sm">
            {/* Tournament buttons */}
            <div className="flex items-center justify-center flex-wrap gap-2 overflow-x-auto">
              {displayedLeagues.map((tournament) => (
                <motion.button
                  key={tournament.value}
                  onClick={() => onSortChange(tournament.value)}
                  className={`
                    px-3 py-2 md:px-4 md:py-2 
                    rounded-lg font-medium text-xs md:text-sm 
                    whitespace-nowrap transition-all duration-200 
                    ${
                      sortType === tournament.value
                        ? 'bg-primary text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                    }
                  `}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {tournament.label}
                </motion.button>
              ))}
              
              {hasMore && (
                <motion.button
                  onClick={onToggleAllLeagues} // Correctly call the handler from props
                  className="
                    px-3 py-2 md:px-4 md:py-2 
                    rounded-lg font-medium text-xs md:text-sm
                    bg-gray-200 dark:bg-gray-600 
                    text-gray-600 dark:text-gray-400 
                    hover:bg-gray-300 dark:hover:bg-gray-500 
                    transition-all duration-200
                  "
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {showAllLeagues ? '← Less' : 'More →'}
                </motion.button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Skeleton для кнопок при загрузке */}
      {!predictions && (
        <div className="mb-6">
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-3 md:p-4 shadow-sm">
            <div className="flex items-center justify-center flex-wrap gap-2 overflow-x-auto animate-pulse">
              {[1, 2, 3, 4].map((index) => (
                <div
                  key={index}
                  className="skeleton w-20 md:w-24 h-8 md:h-9 rounded-lg"
                ></div>
              ))}
              <div className="skeleton w-16 h-8 md:h-9 rounded-lg"></div>
            </div>
          </div>
        </div>
      )}

      {/* Отображаем прогнозы, сгруппированные по дате */}
      {Object.entries(predictions).map(([date, dayPredictions]) => (
        <div key={date} className="mb-6">
          {/* Заголовок с датой */}
          <motion.h2 
            className="text-lg sm:text-xl font-bold mb-4 border-b border-gray-200 dark:border-gray-700 pb-2 flex items-center gap-3"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.3 }}
          >
            <span className="truncate">{formatMediumDate(date)}</span>
            <span className="text-xs sm:text-sm font-medium px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 rounded-full border border-gray-200 dark:border-gray-600">
              {dayPredictions.length}
            </span>
            {/* Показываем текущий фильтр лиги, если не "все" */}
            {sortType !== 'time' && sortType !== 'league' && (
              <span className="text-xs sm:text-sm font-medium px-2 py-1 bg-primary/10 text-primary rounded-full border border-primary/20">
                {LEAGUE_DISPLAY_NAMES[sortType] || sortType}
              </span>
            )}
          </motion.h2>
          
          {/* Список прогнозов для текущей даты */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ staggerChildren: 0.1 }}
            className="grid grid-cols-1 lg:grid-cols-2 gap-4"
          >
            {dayPredictions.map((prediction, index) => {
              // Вычисляем глобальный индекс прогноза, используя новый плоский массив
              const globalIndex = allPredictionsFlat.findIndex(p => p._id === prediction._id);
              
              return (
                <React.Fragment key={prediction._id}>
                  <MatchCard prediction={prediction} />
                  
                  {/* Показываем WelcomeBonus после 4-го прогноза */}
                  {globalIndex === 3 && ( // 4 - номер прогноза, после которого показывается бонус
                    <div className="col-span-full">
                      <WelcomeBonus />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </motion.div>
        </div>
      ))}

      {/* Сообщение, если нет прогнозов после фильтрации */}
      {Object.keys(predictions).length === 0 && sortType !== 'time' && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-10"
        >
          <p className="text-gray-500 dark:text-gray-400">
            No predictions found for {LEAGUE_DISPLAY_NAMES[sortType] || sortType}.
          </p>
          <button 
            onClick={() => onSortChange('time')}
            className="text-primary hover:text-primary-dark text-sm mt-2 underline"
          >
            Show all predictions
          </button>
        </motion.div>
      )}
    </div>
  );
});

// Компонент скелета загрузки для списка прогнозов
export const PredictionListSkeleton = () => {
  // Создаем массив с тремя элементами для скелетов
  const skeletons = [1, 2, 3];
  
  return (
    <div className="pt-4">
      {/* Скелет для заголовка с датой */}
      <div className="skeleton w-48 h-6 mb-4 rounded"></div>
      
      {/* Скелеты карточек */}
      {skeletons.map((index) => (
        <div key={index} className="card mb-4 animate-pulse">
          <div className="flex justify-between mb-2 pb-2 border-b border-gray-200 dark:border-gray-700">
            <div className="skeleton w-32 h-5"></div>
            <div className="skeleton w-24 h-4"></div>
          </div>
          
          <div className="my-4">
            <div className="skeleton w-3/4 h-4 mb-2"></div>
            <div className="skeleton w-full h-3 mb-1"></div>
            <div className="skeleton w-5/6 h-3 mb-1"></div>
            <div className="skeleton w-4/6 h-3 mb-1"></div>
          </div>
          
          <div className="grid grid-cols-3 gap-2 mt-3 mb-2">
            <div className="skeleton h-14"></div>
            <div className="skeleton h-14"></div>
            <div className="skeleton h-14"></div>
          </div>
          
          <div className="flex justify-between items-center mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
            <div className="flex space-x-3">
              <div className="skeleton w-12 h-4"></div>
              <div className="skeleton w-12 h-4"></div>
            </div>
            <div className="skeleton w-20 h-8 rounded-md"></div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default PredictionList; 